"use client";

import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import * as XLSX from "xlsx";
import { useTranslation } from "react-i18next";
import { Button } from "@stockline/ui/components/button";
import {
  ZoomIn,
  ZoomOut,
  Download,
  Grid3x3,
  EyeOff,
  X,
  LayoutList,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { ExcelColumnsMenu } from "./ExcelColumnsMenu";
import { ExcelColumnHeaderMenu } from "./ExcelColumnHeaderMenu";
import { useSheetView } from "./useSheetView";
import { useSheetFilterSort } from "./useSheetFilterSort";
import { useCellSelection } from "./useCellSelection";
import { formatCellValue, getColumnHeader } from "./excel-parse";
import { useRevealHighlightedRow } from "./useRevealHighlightedRow";
import { RESTING_FILL } from "./regionGeometry";

interface ExcelViewerProps {
  data: ArrayBuffer | Uint8Array;
  filename: string;
  className?: string;
  onClose?: () => void;
  /**
   * Optional second Excel buffer (e.g. a per-order slice built from
   * `source_rows`, STK-1269). When provided, a toggle button appears IN this
   * viewer's toolbar and flips which buffer is rendered — the SAME spreadsheet
   * renderer + toolbar drive both, so the slice looks identical to the file and
   * every tool (zoom/filter/grid/fit/download) keeps working in either mode.
   */
  extraData?: ArrayBuffer | Uint8Array;
  /**
   * Pre-translated label for the extra-buffer toggle button shown when the FULL
   * file is on screen (i.e. the action switches to the per-order slice —
   * "Show only this order's lines").
   */
  extraDataLabel?: string;
  /**
   * Pre-translated label shown while the per-order SLICE is on screen (the
   * action switches back to the full file — "Show all"). Falls back to
   * `extraDataLabel` when omitted, so the button text stays static unless a
   * caller opts into the adaptive label.
   */
  extraDataShowAllLabel?: string;
  /** Download filename to use while the extra buffer is the active view. */
  extraFilename?: string;
  /**
   * 0-based index of the column in `extraData` that holds each row's row number
   * in the ORIGINAL document. `highlightedRows` / `restingRows` / `onRowHover`
   * all speak SOURCE-document row numbers, but a slice renumbers its rows
   * densely from 1 — so without this the slice's gutter number is looked up as
   * a source ordinal and rings an unrelated flow (cubic/greptile P1). Set it
   * and the slice translates through that column instead; omit it and the
   * rendered gutter number is used as-is (the plain-file behavior).
   */
  extraDataSourceRowColumn?: number;
  /** 1-based source-document rows to highlight (hovered flow's delineation.rows). */
  highlightedRows?: number[];
  /** 1-based source-document rows shown in the neutral resting tint. */
  restingRows?: number[];
  /** Fires with the source-document row number under the pointer, null on leave. */
  onRowHover?: (sourceRow: number | null) => void;
}

const ROW_HEADER_WIDTH = 50;
const HEADER_ROW_HEIGHT = 25;
const MIN_COL_WIDTH = 30;
const MIN_ROW_HEIGHT = 16;

/** Strong hover tint — the amber every other surface uses for the hovered
 *  flow's region, as a colour rather than a Tailwind class so it can be
 *  composited over a cell's own workbook fill. */
const HOVER_FILL = "rgba(253, 230, 138, 0.6)";

/** The tint a row carries, or null when it carries none. Hover always wins
 *  over resting: a row cited by the hovered flow AND by another must read as
 *  the live hover. */
function rowTint(isHighlighted: boolean, isResting: boolean): string | null {
  if (isHighlighted) return HOVER_FILL;
  return isResting ? RESTING_FILL : null;
}

function rowTintTestId(
  isHighlighted: boolean,
  isResting: boolean,
): string | undefined {
  if (isHighlighted) return "excel-row-highlighted";
  return isResting ? "excel-row-resting" : undefined;
}

export function ExcelViewer({
  data,
  filename,
  className,
  onClose,
  extraData,
  extraDataLabel,
  extraDataShowAllLabel,
  extraFilename,
  extraDataSourceRowColumn,
  highlightedRows,
  restingRows,
  onRowHover,
}: ExcelViewerProps) {
  const { t, i18n } = useTranslation();
  const [zoom, setZoom] = useState(100);
  const [showGrid, setShowGrid] = useState(true);
  // Whether the extra buffer (per-order slice) is the active view vs the file.
  // Defaults ON whenever a slice is supplied so the preview opens showing only
  // the focused order's lines (STK-1269).
  const [viewingExtra, setViewingExtra] = useState(!!extraData);

  // Each buffer is parsed ONCE and owns its own editable state. Toggling the
  // view just picks the other instance — no re-parse of the (potentially huge)
  // file, no reset of the user's resizing / hidden columns. See useSheetView.
  const fileView = useSheetView(data);
  const extraView = useSheetView(extraData);
  // The extra buffer only actually renders once it parsed into a workbook.
  // Derive ONE "showing extra" flag from that so the toggle-button state and the
  // download filename can never claim the slice while the base file is really on
  // screen — which happens when `extraData` is present but invalid (STK-1269).
  const showingExtra = viewingExtra && extraView.hasWorkbook;
  const active = showingExtra ? extraView : fileView;
  const {
    workbook,
    sheetNames: sheets,
    activeSheet,
    setActiveSheet,
    sheetData,
    columnWidths,
    setColumnWidths,
    rowHeights,
    setRowHeights,
    rowNumbers,
    resetSizes,
    hiddenColumns,
    visibleColumnIndices,
    toggleColumn,
    showAllColumns,
    hideAllColumns,
    getColumnLabel,
  } = active;
  const columnCount = columnWidths.length;

  // Excel-like per-column sort + value filter. Operates on the parsed sheet and
  // returns the rows to render (header frozen on top, body filtered + sorted)
  // plus the row-number / height / original-index arrays kept aligned to them.
  const fs = useSheetFilterSort(
    sheetData,
    rowNumbers,
    rowHeights,
    // Reset filters/sort when the rendered content swaps (sheet tab or
    // file↔slice buffer) so a filter set on one view never empties another.
    `${showingExtra ? "x" : "f"}:${activeSheet}`,
  );
  const {
    displayData,
    displayRowNumbers,
    displayRowHeights,
    displayOrigIndex,
  } = fs;
  // Signature of the rendered view (the ordered/filtered physical row numbers)
  // so a changed filter/sort resets the selection rectangle — its positions
  // would otherwise point at now-reordered rows.
  const filterSortKey = useMemo(
    () => displayRowNumbers.join(","),
    [displayRowNumbers],
  );

  // Excel-like cell selection + sum/avg/count (STK-1269 follow-up). Keyed on the
  // active sheet + which buffer is showing + the filter/sort view so a stale
  // rectangle never lingers when the rendered content swaps or reorders.
  const selection = useCellSelection({
    sheetData: displayData,
    visibleColumnIndices,
    resetKey: `${showingExtra ? "x" : "f"}:${activeSheet}:${filterSortKey}`,
  });

  // Locale-aware number formatting (fr → comma decimal / space thousands). Uses
  // the i18n language, never the browser locale.
  const numberFormatter = useMemo(
    () =>
      new Intl.NumberFormat(i18n.language, {
        maximumFractionDigits: 2,
      }),
    [i18n.language],
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  // The sticky column/row header row — measured live so the reveal below never
  // lands a row underneath its opaque paint (STK-1602 finding 2).
  const theadRef = useRef<HTMLTableSectionElement>(null);
  // Tracks the in-progress drag's cleanup so we can run it if the component
  // unmounts mid-drag (cursor/userSelect would otherwise stay stuck globally).
  const dragCleanupRef = useRef<(() => void) | null>(null);

  // Re-assert the default whenever the slice buffer changes: switching the
  // active order (e.g. via the panel's prev/next navigation) rebuilds the slice
  // into a NEW `extraData` reference — snap the view back to that order's lines
  // so the preview always shows only the focused order (STK-1269). When the new
  // order has no slice, this falls back to the file (extraData undefined).
  useEffect(() => {
    setViewingExtra(!!extraData);
  }, [extraData]);

  // A rendered row's number in the SOURCE document. For the plain file the two
  // spaces coincide; a slice renumbers densely from 1 and carries the real
  // position in `extraDataSourceRowColumn`, so that column IS the translation
  // (cubic/greptile P1 — without it a slice row rings an unrelated flow).
  const sourceRowOfRendered = useCallback(
    (rowIndex: number): number | null => {
      if (!showingExtra || extraDataSourceRowColumn === undefined) {
        return displayRowNumbers[rowIndex] ?? null;
      }
      const cell = displayData[rowIndex]?.[extraDataSourceRowColumn];
      const raw = cell?.numericValue ?? Number(cell?.value);
      return Number.isFinite(raw) && raw > 0 ? raw : null;
    },
    [showingExtra, extraDataSourceRowColumn, displayRowNumbers, displayData],
  );

  // source-document row → rendered gutter number. Highlight/resting inputs are
  // mapped through this ONCE here so everything downstream (the tint test and
  // the reveal's `[data-row-number]` lookup) stays in rendered-row space.
  const renderedBySourceRow = useMemo(() => {
    const map = new Map<number, number>();
    displayRowNumbers.forEach((rendered, i) => {
      const source = sourceRowOfRendered(i);
      if (source !== null) map.set(source, rendered);
    });
    return map;
  }, [displayRowNumbers, sourceRowOfRendered]);

  const toRenderedRows = useCallback(
    (rows: number[] | undefined): number[] =>
      (rows ?? [])
        .map((row) => renderedBySourceRow.get(row))
        .filter((row): row is number => row !== undefined),
    [renderedBySourceRow],
  );

  const renderedHighlightedRows = useMemo(
    () => toRenderedRows(highlightedRows),
    [toRenderedRows, highlightedRows],
  );
  const renderedRestingRows = useMemo(
    () => toRenderedRows(restingRows),
    [toRenderedRows, restingRows],
  );

  // Scroll the hovered flow's topmost source row into view — but ONLY when it
  // is off screen, and never underneath the sticky header (STK-1602). See
  // useRevealHighlightedRow.ts for the dependency contract and inset math.
  const { hi } = useRevealHighlightedRow({
    highlightedRows: renderedHighlightedRows,
    rowNumbers: displayRowNumbers,
    activeSheet,
    containerRef,
    headerRef: theadRef,
  });

  // Every flow's source rows, shown in the fainter resting tint while the
  // pointer is inside the preview (STK-2338). Hover always wins over resting —
  // see the `hi.has` check ordering below.
  const resting = useMemo(
    () => new Set(renderedRestingRows),
    [renderedRestingRows],
  );

  // Zoom controls
  const handleZoom = (newZoom: number) => {
    setZoom(Math.max(25, Math.min(400, newZoom)));
  };

  const resetZoom = () => {
    setZoom(100);
  };

  // Ctrl/⌘ + wheel zoom
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (ev: WheelEvent) => {
      if (!ev.ctrlKey && !ev.metaKey) return;
      ev.preventDefault();
      const step = ev.deltaY < 0 ? 10 : -10;
      setZoom((z) => Math.max(25, Math.min(400, z + step)));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  // Drag-to-resize columns and rows. Mouse coords are in screen space, but the
  // table is rendered inside a transform: scale(zoom/100) wrapper, so we divide
  // the delta by the scale factor to get the logical (unscaled) size delta.
  const startResize = useCallback(
    (type: "col" | "row", index: number, startCoord: number) => {
      // If a previous drag is somehow still active, tear it down first.
      dragCleanupRef.current?.();
      const initial =
        type === "col" ? columnWidths[index] || 100 : rowHeights[index] || 25;
      const factor = Math.max(0.01, zoom / 100);
      const minSize = type === "col" ? MIN_COL_WIDTH : MIN_ROW_HEIGHT;
      const setter = type === "col" ? setColumnWidths : setRowHeights;
      const onMove = (ev: MouseEvent) => {
        const current = type === "col" ? ev.clientX : ev.clientY;
        const next = Math.max(
          minSize,
          initial + (current - startCoord) / factor,
        );
        setter((prev) => {
          const arr = [...prev];
          arr[index] = next;
          return arr;
        });
      };
      const cleanup = () => {
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", cleanup);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
        dragCleanupRef.current = null;
      };
      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", cleanup);
      document.body.style.cursor = type === "col" ? "col-resize" : "row-resize";
      document.body.style.userSelect = "none";
      dragCleanupRef.current = cleanup;
    },
    [columnWidths, rowHeights, zoom, setColumnWidths, setRowHeights],
  );

  // Ensure a drag-in-progress is cleaned up if the viewer unmounts.
  useEffect(() => {
    return () => {
      dragCleanupRef.current?.();
    };
  }, []);

  const autoFitColumn = useCallback(
    (colIndex: number) => {
      let max = 60;
      for (const row of sheetData) {
        const cell = row?.[colIndex];
        if (!cell) continue;
        const text = formatCellValue(cell);
        max = Math.max(max, Math.min(400, 16 + text.length * 7));
      }
      setColumnWidths((prev) => {
        const arr = [...prev];
        arr[colIndex] = max;
        return arr;
      });
    },
    [sheetData, setColumnWidths],
  );

  // Total natural dimensions (used so the scroll container reserves the right
  // amount of space when the table is scaled via CSS transform).
  const naturalWidth = useMemo(
    () =>
      ROW_HEADER_WIDTH +
      visibleColumnIndices.reduce(
        (sum, ci) => sum + (columnWidths[ci] || 100),
        0,
      ),
    [visibleColumnIndices, columnWidths],
  );
  const naturalHeight = useMemo(
    () =>
      HEADER_ROW_HEIGHT +
      displayRowHeights.reduce((sum, h) => sum + (h || 25), 0),
    [displayRowHeights],
  );

  // Download the ACTIVE workbook — so in the slice view the user gets the
  // per-order rows, and in file view the full file. Filename follows suit.
  const handleDownload = () => {
    if (!workbook) return;

    const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/octet-stream" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = showingExtra ? (extraFilename ?? filename) : filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className={cn("flex flex-col h-full bg-white", className)}>
      {/* Toolbar — view tools on the left, file action on the right */}
      <div className="flex items-center gap-1.5 px-3 py-1.5 border-b">
        {/* Per-order source-rows toggle (STK-1269). It flips the active buffer
            between the file and the slice — both render through the SAME
            spreadsheet body below, so every tool keeps working and the toolbar
            is identical in either mode. The label states the ACTION: while the
            slice is on screen it reads "Show all" (switch to the file); while
            the file is on screen it reads "Show only …" (switch to the slice).
            Falls back to the single label when no show-all label is supplied. */}
        {extraData && (
          <Button
            variant={showingExtra ? "default" : "outline"}
            size="sm"
            onClick={() => setViewingExtra((v) => !v)}
            className="h-7 px-2 text-xs"
            data-testid="source-rows-view-toggle"
          >
            <LayoutList className="h-4 w-4 mr-1.5" />
            {showingExtra
              ? (extraDataShowAllLabel ?? extraDataLabel)
              : extraDataLabel}
          </Button>
        )}

        {/* Zoom (compact stepper; the % doubles as reset) */}
        <div className="flex items-center rounded-md border">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleZoom(zoom - 10)}
            className="h-7 w-7 p-0 rounded-r-none"
            title={t("documents.viewer.zoomOut")}
          >
            <ZoomOut className="h-4 w-4" />
          </Button>
          <button
            type="button"
            onClick={resetZoom}
            title={t("documents.viewer.resetZoom")}
            className="h-7 min-w-[3rem] px-1 text-xs tabular-nums text-gray-600 hover:bg-gray-100"
          >
            {zoom}%
          </button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleZoom(zoom + 10)}
            className="h-7 w-7 p-0 rounded-l-none"
            title={t("documents.viewer.zoomIn")}
          >
            <ZoomIn className="h-4 w-4" />
          </Button>
        </div>

        {/* Column filter (show / hide) */}
        <ExcelColumnsMenu
          columnCount={columnCount}
          getLabel={getColumnLabel}
          hiddenColumns={hiddenColumns}
          onToggle={toggleColumn}
          onShowAll={showAllColumns}
          onHideAll={hideAllColumns}
        />

        {/* Grid toggle */}
        <Button
          variant={showGrid ? "secondary" : "ghost"}
          size="sm"
          onClick={() => setShowGrid(!showGrid)}
          className="h-7 w-7 p-0"
          title={t("documents.viewer.toggleGrid")}
        >
          <Grid3x3 className="h-4 w-4" />
        </Button>

        {/* Reset column/row sizes */}
        <Button
          variant="ghost"
          size="sm"
          onClick={resetSizes}
          className="h-7 px-2 text-xs"
          title={t("documents.viewer.fitTitle")}
        >
          {t("documents.viewer.fit")}
        </Button>

        {/* Clear all column filters + sort (only shown when something is active) */}
        {fs.anyActive && (
          <Button
            variant="ghost"
            size="sm"
            onClick={fs.clearAll}
            className="h-7 px-2 text-xs text-blue-600 hover:text-blue-700"
            title={t("documents.viewer.clearFiltersSort")}
            data-testid="excel-clear-filters"
          >
            {t("documents.viewer.clearFiltersSort")}
          </Button>
        )}

        {/* Download (icon only) */}
        <Button
          variant="ghost"
          size="sm"
          onClick={handleDownload}
          className="h-7 w-7 p-0 ml-auto"
          title={t("documents.viewer.download")}
        >
          <Download className="h-4 w-4" />
        </Button>

        {onClose && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-7 w-7 p-0"
            title={t("common.close")}
            aria-label={t("common.close")}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Body — always the real spreadsheet, rendering whichever buffer is
          active (file or per-order slice). One render path for both. */}
      {/* Sheet tabs */}
      {sheets.length > 1 && (
        <div className="flex items-center gap-1 px-3 py-2 border-b bg-gray-50 overflow-x-auto">
          <div className="flex gap-1">
            {sheets.map((sheetName) => (
              <button
                key={sheetName}
                type="button"
                onClick={() => setActiveSheet(sheetName)}
                className={cn(
                  "px-3 py-1 text-xs rounded-t-md border border-b-0 transition-colors",
                  activeSheet === sheetName
                    ? "bg-white text-blue-600 border-gray-300"
                    : "bg-gray-100 text-gray-600 border-gray-200 hover:bg-gray-200",
                )}
              >
                {sheetName}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Spreadsheet container */}
      <div
        ref={containerRef}
        className="flex-1 overflow-auto relative"
        style={{ backgroundColor: "#f9fafb" }}
      >
        {/* Outer wrapper sized to scaled dimensions so scrollbars cover the
            zoomed content. Inner wrapper applies the scale transform. */}
        <div
          style={{
            width: naturalWidth * (zoom / 100),
            height: naturalHeight * (zoom / 100),
          }}
        >
          <div
            ref={tableRef}
            style={{
              transform: `scale(${zoom / 100})`,
              transformOrigin: "top left",
              width: naturalWidth,
            }}
          >
            <table
              className={cn(
                "border-collapse bg-white",
                showGrid && "border border-gray-300",
              )}
              style={{ tableLayout: "fixed" }}
            >
              <thead ref={theadRef} className="sticky top-0 z-10">
                <tr>
                  {/* Top-left corner cell */}
                  <th
                    className={cn(
                      "bg-gray-100 font-normal text-xs text-gray-600 sticky left-0 z-20",
                      showGrid && "border border-gray-300",
                    )}
                    style={{
                      width: ROW_HEADER_WIDTH,
                      height: HEADER_ROW_HEIGHT,
                    }}
                  >
                    {/* Empty corner */}
                  </th>
                  {/* Column headers (hidden columns are skipped) */}
                  {visibleColumnIndices.map((colIndex, colPos) => {
                    const width = columnWidths[colIndex];
                    return (
                      <th
                        key={colIndex}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          selection.selectColumn(colPos, e.shiftKey);
                        }}
                        className={cn(
                          "group bg-gray-100 font-normal text-xs text-gray-600 relative select-none cursor-pointer",
                          showGrid && "border border-gray-300",
                        )}
                        style={{
                          width: width || 100,
                          minWidth: width || 100,
                          height: HEADER_ROW_HEIGHT,
                        }}
                      >
                        {getColumnHeader(colIndex)}
                        {/* Hide-this-column affordance (appears on hover) */}
                        <button
                          type="button"
                          title={t("documents.viewer.hideColumn")}
                          aria-label={t("documents.viewer.hideColumnAria", {
                            col: getColumnHeader(colIndex),
                          })}
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleColumn(colIndex);
                          }}
                          className="absolute left-0.5 top-1/2 -translate-y-1/2 z-30 inline-flex h-4 w-4 items-center justify-center rounded bg-white/80 text-gray-500 opacity-0 hover:bg-gray-200 hover:text-gray-800 group-hover:opacity-100"
                        >
                          <EyeOff className="h-3 w-3" />
                        </button>
                        {/* Excel-like sort + value filter for this column. */}
                        <ExcelColumnHeaderMenu
                          colIndex={colIndex}
                          columnLabel={getColumnHeader(colIndex)}
                          sortDirection={
                            fs.sort?.colIndex === colIndex
                              ? fs.sort.direction
                              : null
                          }
                          onSort={fs.setSort}
                          values={fs.getColumnValues(colIndex)}
                          selected={fs.getSelectedValues(colIndex)}
                          onFilterChange={fs.setColumnFilter}
                          isFiltered={fs.isColumnFiltered(colIndex)}
                        />
                        <div
                          role="separator"
                          aria-orientation="vertical"
                          title="Drag to resize · double-click to auto-fit"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            startResize("col", colIndex, e.clientX);
                          }}
                          onDoubleClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            autoFitColumn(colIndex);
                          }}
                          className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-blue-400 z-30"
                        />
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {displayData.map((row, rowIndex) => {
                  const physicalRow = displayRowNumbers[rowIndex];
                  const origIndex = displayOrigIndex[rowIndex];
                  const isHighlighted = hi.has(physicalRow);
                  const isResting = !isHighlighted && resting.has(physicalRow);
                  // Painted per CELL as a gradient overlay, not as a row
                  // background: a cell carries its own `backgroundColor` from
                  // the workbook's fill, which sits ABOVE the row's paint and
                  // hid the tint entirely on any styled sheet (cubic P2).
                  const tint = rowTint(isHighlighted, isResting);
                  return (
                    <tr
                      key={physicalRow}
                      data-row-number={physicalRow}
                      onMouseEnter={() =>
                        onRowHover?.(sourceRowOfRendered(rowIndex))
                      }
                      onMouseLeave={() => onRowHover?.(null)}
                      data-testid={rowTintTestId(isHighlighted, isResting)}
                    >
                      {/* Row number with bottom resize handle */}
                      <td
                        onMouseDown={(e) => {
                          e.preventDefault();
                          selection.selectRow(rowIndex, e.shiftKey);
                        }}
                        className={cn(
                          "bg-gray-100 font-normal text-xs text-gray-600 text-center sticky left-0 z-10 relative select-none cursor-pointer",
                          showGrid && "border border-gray-300",
                        )}
                        style={{
                          width: ROW_HEADER_WIDTH,
                          height: displayRowHeights[rowIndex] || 25,
                        }}
                      >
                        {physicalRow}
                        <div
                          role="separator"
                          aria-orientation="horizontal"
                          title="Drag to resize row"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            startResize("row", origIndex, e.clientY);
                          }}
                          className="absolute bottom-0 left-0 right-0 h-1.5 cursor-row-resize hover:bg-blue-400 z-20"
                        />
                      </td>
                      {/* Cells (hidden columns are skipped) */}
                      {visibleColumnIndices.map((colIndex, colPos) => {
                        const cell = row[colIndex];
                        const selected = selection.isSelected(rowIndex, colPos);
                        const isActiveCell = selection.isActive(
                          rowIndex,
                          colPos,
                        );
                        return (
                          <td
                            key={colIndex}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              selection.beginSelect(
                                rowIndex,
                                colPos,
                                e.shiftKey,
                              );
                            }}
                            onMouseEnter={() =>
                              selection.extendTo(rowIndex, colPos)
                            }
                            className={cn(
                              "px-2 py-1 text-sm select-none",
                              showGrid && "border border-gray-200",
                              selected && "bg-blue-500/15",
                              isActiveCell && "ring-1 ring-inset ring-blue-500",
                            )}
                            style={{
                              ...cell?.style,
                              // AFTER the workbook's own style: a flat
                              // linear-gradient composites over
                              // `background-color`, so the tint reads on a
                              // filled cell as well as an unfilled one.
                              ...(tint
                                ? {
                                    backgroundImage: `linear-gradient(${tint}, ${tint})`,
                                  }
                                : {}),
                              width: columnWidths[colIndex] || 100,
                              minWidth: columnWidths[colIndex] || 100,
                              height: displayRowHeights[rowIndex] || 25,
                              whiteSpace: "nowrap" as const,
                              overflow: "hidden" as const,
                              textOverflow: "ellipsis" as const,
                            }}
                            title={
                              cell?.formula
                                ? `Formula: ${cell.formula}`
                                : formatCellValue(cell)
                            }
                          >
                            {formatCellValue(cell)}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Status bar */}
      <div className="flex items-center justify-between px-3 py-2 border-t bg-gray-50 text-xs text-gray-600">
        <div className="flex items-center gap-4">
          <span>{t("documents.viewer.sheet", { name: activeSheet })}</span>
          <span>
            {t("documents.viewer.dimensions", {
              rows: displayData.length,
              cols: columnCount,
            })}
            {hiddenColumns.size > 0 &&
              ` ${t("documents.viewer.hiddenSuffix", {
                count: hiddenColumns.size,
              })}`}
            {displayData.length < sheetData.length &&
              ` ${t("documents.viewer.filteredSuffix", {
                total: sheetData.length,
              })}`}
          </span>
        </div>
        <div className="flex items-center gap-4">
          {(selection.stats.numericCount > 0 ||
            selection.stats.cellCount > 1) && (
            <div
              className="flex items-center gap-3 tabular-nums"
              data-testid="excel-selection-stats"
            >
              {selection.stats.numericCount > 0 && (
                <>
                  <span>
                    {t("documents.viewer.selectionSum", {
                      value: numberFormatter.format(selection.stats.sum),
                    })}
                  </span>
                  <span>
                    {t("documents.viewer.selectionAvg", {
                      value: numberFormatter.format(selection.stats.average),
                    })}
                  </span>
                </>
              )}
              <span>
                {t("documents.viewer.selectionCount", {
                  count: selection.stats.cellCount,
                })}
              </span>
            </div>
          )}
          <span>{t("documents.viewer.zoomLabel", { zoom })}</span>
        </div>
      </div>
    </div>
  );
}
